import { XMarkIcon } from "@heroicons/react/24/outline";
import { Input, Typography } from "@material-tailwind/react";
import Label from "components/Label/Label";
import { AnimatePresence, motion } from "framer-motion";
import { useAppDispatch } from "hooks/hooks";
import { Tag } from "lucide-react";
import { FC } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import ButtonPrimary from "shared/Button/ButtonPrimary";
import ButtonSecondary from "shared/Button/ButtonSecondary";
import { z } from "zod";
import { refundSubscription } from "redux/features/subscription/subscriptionSlice";
import { EASING } from "../../../utils/easingUtils";

const planSchema = z.object({
  amount: z
    .number({
      required_error: "Le montant est requis",
      invalid_type_error: "Le montant doit être un nombre",
    })
    .nonnegative("Le montant doit être positif"),
});

type PlanSchemaType = z.infer<typeof planSchema>;

type RefundSubscriptionModalProps = {
  open: boolean;
  subscription: {
    payment_intent: string;
    // You can expand this type according to your data shape
  } | null;
  handleClose: () => void;
};

export const RefundSubscriptionModal: FC<RefundSubscriptionModalProps> = ({ open, subscription, handleClose }) => {
  const dispatch = useAppDispatch();

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<PlanSchemaType>({
    resolver: zodResolver(planSchema),
  });

  const onSubmit = (data: PlanSchemaType) => {
    if (!subscription) return;
    dispatch(
      refundSubscription({
        payment_id: subscription.payment_intent,
        amount: data.amount,
      })
    );
    handleClose();
  };

  if (!open || !subscription) return null;

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.3 }}
          className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-60 backdrop-blur-sm"
          role="dialog"
          aria-labelledby="modal-title"
          aria-modal="true"
        >
          <motion.div
            initial={{ scale: 0.9, opacity: 0, y: 20 }}
            animate={{ scale: 1, opacity: 1, y: 0 }}
            exit={{ scale: 0.9, opacity: 0, y: 20 }}
            transition={{ duration: 0.3, ease: EASING.easeOut }}
            className="w-full max-w-md p-6 bg-white shadow-2xl dark:bg-gray-900 rounded-2xl"
          >
            <div className="flex items-center justify-between mb-6">
              <h3
                id="modal-title"
                className="flex items-center gap-2 text-xl font-semibold text-gray-900 dark:text-white"
              >
                <Tag className="w-6 h-6 text-blue-500" />
                Remboursement
              </h3>
              <button
                onClick={handleClose}
                className="p-1 text-gray-500 transition-colors hover:text-gray-700 dark:text-gray-400 dark:hover:text-white"
                aria-label="Fermer la fenêtre modale"
              >
                <XMarkIcon className="w-6 h-6" />
              </button>
            </div>

            <form onSubmit={handleSubmit(onSubmit)}>
              <motion.div initial="hidden" animate="visible">
                <Label className="font-semibold text-gray-700 dark:text-gray-200">Montant :</Label>
                <div className="relative mt-2">
                  <Input
                    size="lg"
                    type="number"
                    step="0.01"
                    placeholder="Ex. 99.99"
                    {...register("amount", { valueAsNumber: true })}
                    aria-invalid={!!errors.amount}
                    aria-describedby="amount-error"
                    className="text-gray-900 transition-shadow duration-200 rounded-lg shadow-sm bg-gray-50 dark:bg-gray-800 dark:text-white hover:shadow-md focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400"
                  />
                </div>
                <AnimatePresence>
                  {errors.amount && (
                    <motion.div
                      initial={{ opacity: 0, y: -10 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -10 }}
                    >
                      <Typography
                        id="amount-error"
                        variant="small"
                        color="red"
                        className="mt-1 text-xs text-red-500 dark:text-red-400"
                      >
                        {errors.amount.message}
                      </Typography>
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.div>

              <div className="flex justify-end gap-3 mt-6">
                <ButtonSecondary
                  type="button"
                  onClick={handleClose}
                  className="px-5 py-2.5 text-gray-700 bg-gray-200 dark:bg-gray-700 dark:text-gray-200 rounded-lg hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors"
                >
                  Annuler
                </ButtonSecondary>
                <ButtonPrimary
                  type="submit"
                  className="px-5 py-2.5 text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors"
                >
                  Confirmer
                </ButtonPrimary>
              </div>
            </form>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
